import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PhoneInput } from 'react-international-phone';
import { PhoneNumberUtil } from 'google-libphonenumber';
import 'react-international-phone/style.css';
import { apiService } from '../services/api';
import { useRecaptchaEnterprise } from '../hooks/useRecaptchaEnterprise';
import { isAndroid } from '../utils/browserContext';

interface HandoffCompany {
  name?: string;
  logo?: string;
  country?: string;
}

interface HandoffPosition {
  uuid: string;
  name: string;
}

interface WhatsAppHandoffProps {
  url: string;
  phone: string;
  translations?: Record<string, string> | null;
  company?: HandoffCompany | null;
  clientUuid?: string | null;
  position?: HandoffPosition | null;
  trackingCode?: string | null;
}

type Stage = 'ready' | 'trying' | 'exhausted';
type FormStage = 'idle' | 'sending' | 'sent';

const HANDOFF_TIMEOUT_MS = 1500;
const RECAPTCHA_READY_TIMEOUT_MS = 3000;

const messageFrom = (waMeUrl: string): string => {
  try {
    return new URL(waMeUrl).searchParams.get('text') || '';
  } catch {
    return '';
  }
};

const selfUrl = () => `${window.location.host}${window.location.pathname}${window.location.search}`;

const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (value: string) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(value));
  } catch {
    return false;
  }
};

/**
 * A browser hands a link to the WhatsApp app only for a navigation it treats as
 * user-initiated, so the tap is what makes the handoff work. Android gets an
 * attempt on mount too: its intent URL carries a fallback, so a missing app
 * lands on wa.me rather than on a system error.
 */
export const WhatsAppHandoff: React.FC<WhatsAppHandoffProps> = ({
  url,
  phone,
  translations,
  company,
  clientUuid,
  position,
  trackingCode,
}) => {
  const [stage, setStage] = useState<Stage>('ready');
  const autoTried = useRef(false);

  const t = (key: string, fallback: string) => translations?.[key] || fallback;

  const offersForm = Boolean(position && clientUuid);
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  useRecaptchaEnterprise(offersForm ? recaptchaSiteKey : '');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [formStage, setFormStage] = useState<FormStage>('idle');
  const [formError, setFormError] = useState<string | null>(null);

  const message = messageFrom(url);
  const deepLink = isAndroid()
    ? `intent://send/?phone=${phone}&text=${encodeURIComponent(message)}` +
      `#Intent;scheme=whatsapp;package=com.whatsapp;` +
      `S.browser_fallback_url=${encodeURIComponent(url)};end`
    : `whatsapp://send?phone=${phone}&text=${encodeURIComponent(message)}`;

  const attempt = useCallback((target: string, onBlocked?: () => void) => {
    let settled = false;

    const cleanup = () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.clearTimeout(timer);
    };

    function onHidden() {
      if (!document.hidden) return;
      settled = true;
      cleanup();
    }

    const timer = window.setTimeout(() => {
      cleanup();
      if (!settled) onBlocked?.();
    }, HANDOFF_TIMEOUT_MS);

    document.addEventListener('visibilitychange', onHidden);

    try {
      window.location.href = target;
    } catch {
      cleanup();
      onBlocked?.();
    }
  }, []);

  useEffect(() => {
    if (autoTried.current || !isAndroid()) return;
    autoTried.current = true;
    attempt(deepLink);
  }, [attempt, deepLink]);

  const onOpen = () => {
    setStage('trying');
    attempt(deepLink, () => {
      if (isAndroid()) {
        // Leave the in-app browser; the deep link works from a real browser.
        attempt(`intent://${selfUrl()}#Intent;scheme=https;action=android.intent.action.VIEW;end`, () =>
          setStage('exhausted'),
        );
        return;
      }
      setStage('exhausted');
    });
  };

  const recaptchaToken = async (): Promise<string> => {
    const enterprise = (window as any).grecaptcha?.enterprise;
    if (!recaptchaSiteKey || !enterprise) return '';
    try {
      // `grecaptcha.enterprise` is defined the moment the script parses, but a
      // token minted before ready() fires comes back unparseable — Google
      // rejects it as invalid-token, with no action and no hostname. A
      // reCAPTCHA that never signals yields no token rather than that one.
      const ready = await Promise.race([
        new Promise<boolean>((resolve) => enterprise.ready(() => resolve(true))),
        new Promise<boolean>((resolve) =>
          window.setTimeout(() => resolve(false), RECAPTCHA_READY_TIMEOUT_MS),
        ),
      ]);
      if (!ready) return '';
      return await enterprise.execute(recaptchaSiteKey, { action: 'apply_to_job' });
    } catch {
      return '';
    }
  };

  const onSubmitDetails = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!position || !clientUuid || formStage === 'sending') return;

    if (!isPhoneValid(contactPhone)) {
      setFormError(t('whatsapp_handoff.form_invalid_phone', 'Please enter a valid phone number.'));
      return;
    }

    setFormError(null);
    setFormStage('sending');
    try {
      const formData = new FormData();
      formData.set('first_name', firstName.trim());
      formData.set('last_name', lastName.trim());
      formData.set('phone', contactPhone);
      formData.set('killer_questions', '[]');
      formData.set('recaptcha_token', await recaptchaToken());
      if (trackingCode) formData.set('tracking_code', trackingCode);

      await apiService.applyToPosition(clientUuid, position.uuid, formData);
      setFormStage('sent');
    } catch {
      setFormStage('idle');
      setFormError(
        t('whatsapp_handoff.form_error', 'We could not send your details. Please try again.'),
      );
    }
  };

  const logo = company?.logo;
  const companyName = company?.name || '';
  const greeting = position
    ? t('whatsapp_handoff.title_position', 'Thanks for your interest in {position}').replace(
        '{position}',
        position.name,
      )
    : t('whatsapp_handoff.title', 'Thanks for your interest');

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10 bg-[#fafaf7]">
      <div className="w-full max-w-sm text-center">
        {logo ? (
          <img
            src={logo}
            alt={companyName ? `Logo ${companyName}` : ''}
            className="h-20 w-auto mx-auto mb-5 rounded-2xl shadow bg-white border-4 border-white"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <svg className="w-14 h-14 mx-auto mb-5" viewBox="0 0 24 24" fill="#25D366" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.82 9.82 0 016.988 2.9 9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.488" />
          </svg>
        )}

        <h1 className="text-xl font-semibold text-gray-900 mb-1">{greeting}</h1>
        {companyName && <p className="text-sm text-gray-500 mb-4">{companyName}</p>}

        <p className="text-gray-600 mb-7">
          {t(
            'whatsapp_handoff.subtitle',
            "Message us on WhatsApp and we'll get your application started.",
          )}
        </p>

        <button
          type="button"
          onClick={onOpen}
          className="custom-color w-full py-4 rounded-xl text-white text-lg font-semibold"
          style={{ backgroundColor: '#25D366' }}
        >
          {stage === 'trying'
            ? t('whatsapp_handoff.opening', 'Opening WhatsApp…')
            : t('whatsapp_handoff.button', 'Apply now')}
        </button>

        {stage === 'exhausted' && (
          <div className="mt-6 text-sm text-gray-600 border-t border-gray-200 pt-5">
            <p className="mb-3">
              {t(
                'whatsapp_handoff.fallback',
                "Didn't open? Open this page in Chrome or Safari, or message us directly.",
              )}
            </p>
            <a href={url} className="font-semibold underline" style={{ color: '#128C7E' }}>
              {phone}
            </a>
          </div>
        )}

        {offersForm && formStage === 'sent' && (
          <div className="mt-8 border-t border-gray-200 pt-6 text-sm text-gray-700">
            <p className="font-semibold text-gray-900 mb-1">
              {t('whatsapp_handoff.form_sent_title', 'Thanks — we have your details.')}
            </p>
            <p>{t('whatsapp_handoff.form_sent_body', 'We will contact you shortly.')}</p>
          </div>
        )}

        {offersForm && formStage !== 'sent' && (
          <form onSubmit={onSubmitDetails} className="mt-8 border-t border-gray-200 pt-6 text-left">
            <p className="text-sm text-gray-600 text-center mb-5">
              {t('whatsapp_handoff.form_intro', 'Or leave us your number and we will contact you.')}
            </p>

            <label className="block text-sm text-gray-700 mb-1" htmlFor="handoff-first-name">
              {t('whatsapp_handoff.form_first_name', 'First name')}
            </label>
            <input
              id="handoff-first-name"
              type="text"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full mb-4 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200"
            />

            <label className="block text-sm text-gray-700 mb-1" htmlFor="handoff-last-name">
              {t('whatsapp_handoff.form_last_name', 'Last name')}
            </label>
            <input
              id="handoff-last-name"
              type="text"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full mb-4 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200"
            />

            <label className="block text-sm text-gray-700 mb-1">
              {t('whatsapp_handoff.form_phone', 'Phone')}
            </label>
            <div className="mb-4 border border-gray-300 rounded-lg overflow-hidden">
              <PhoneInput
                defaultCountry={(company?.country || 'mx').toLowerCase()}
                value={contactPhone}
                onChange={(value) => setContactPhone(value.toString())}
                inputProps={{
                  name: 'phone',
                  required: true,
                  className: 'w-full px-3 py-2 border-0 focus:outline-none focus:ring-0',
                }}
              />
            </div>

            {formError && <p className="text-sm text-red-600 mb-4">{formError}</p>}

            <button
              type="submit"
              disabled={formStage === 'sending'}
              className="w-full py-3 rounded-xl border border-gray-300 text-gray-800 font-semibold disabled:opacity-60"
            >
              {formStage === 'sending'
                ? t('whatsapp_handoff.form_sending', 'Sending…')
                : t('whatsapp_handoff.form_submit', 'Contact me')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default WhatsAppHandoff;
